(() => {
  const SECTION_ID = 'blog-product-quickview';
  const BIS_MODAL_ID = '#ProductBISModal';
  const loadedScripts = new Set();
  const htmlCache = new Map();
  const inflight = new Map();

  function getHandle(el) {
    return (el.getAttribute('data-product-handle') || el.getAttribute('data-handle') || '').trim();
  }

  function loadScript(src) {
    if (!src || loadedScripts.has(src)) return Promise.resolve();
    if (document.querySelector(`script[src="${src}"]`)) {
      loadedScripts.add(src);
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => {
        loadedScripts.add(src);
        resolve();
      };
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  function ensureComponents() {
    const map = window.components || {};
    const needed = ['modal-dialog', 'modal-opener', 'backinstock-block'];
    return Promise.all(needed.map((name) => (map[name] ? loadScript(map[name]) : Promise.resolve())));
  }

  function nodeFromHtml(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
  }

  async function fetchQuickviewHtml(handle) {
    if (htmlCache.has(handle)) {
      return nodeFromHtml(htmlCache.get(handle));
    }

    if (inflight.has(handle)) {
      await inflight.get(handle);
      return nodeFromHtml(htmlCache.get(handle));
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`;

    const request = (async () => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Unable to load product: ${handle}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector(`#shopify-section-${SECTION_ID}`) || doc.body;
      const card = section.querySelector('[data-blog-product-quickview]');
      if (!card) throw new Error(`Quickview markup missing for: ${handle}`);
      htmlCache.set(handle, card.outerHTML);
    })();

    inflight.set(handle, request);
    try {
      await request;
    } finally {
      inflight.delete(handle);
    }

    return nodeFromHtml(htmlCache.get(handle));
  }

  function parseVariants(card) {
    const el = card.querySelector('[data-bpqv-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  function getSelectedOptions(card) {
    const options = [];
    card.querySelectorAll('.bpqv__option').forEach((optionEl) => {
      const checked = optionEl.querySelector('[data-product-option]:checked');
      if (checked) options.push(checked.value);
    });
    return options;
  }

  function findVariant(variants, selectedOptions) {
    return variants.find((variant) =>
      variant.options.every((opt, index) => opt === selectedOptions[index])
    );
  }

  function syncSharedBisModal(card) {
    const modal = document.querySelector(BIS_MODAL_ID);
    if (!modal) return;
    const select = modal.querySelector("select[name='variant'], select[name='id']");
    const variantSelect = card.querySelector('[data-bpqv-variant-select]');
    if (!select || !variantSelect) return;
    const value = variantSelect.value;
    if ('value' in select) select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function updateActionUi(card, available) {
    const soldout = !available;
    card.classList.toggle('bpqv__inner--soldout', soldout);

    const atc = card.querySelector('[data-bpqv-atc]');
    const buyNow = card.querySelector('[data-bpqv-buy-now]');
    const soldOutBtn = card.querySelector('[data-bpqv-soldout]');
    const bis = card.querySelector('[data-bpqv-bis-opener]');

    if (atc) {
      if (soldout) {
        atc.hidden = true;
        atc.disabled = true;
      } else {
        atc.hidden = false;
        atc.disabled = false;
        atc.textContent = atc.dataset.labelAvailable || atc.textContent;
      }
    }
    if (buyNow) {
      if (soldout) {
        buyNow.hidden = true;
        buyNow.setAttribute('aria-disabled', 'true');
      } else {
        buyNow.hidden = false;
        buyNow.removeAttribute('aria-disabled');
      }
    }
    if (soldOutBtn) soldOutBtn.hidden = !soldout;
    if (bis) {
      if (soldout) bis.removeAttribute('hidden');
      else bis.setAttribute('hidden', '');
    }
  }

  function updateCardState(card, variant) {
    if (!variant) return;
    const select = card.querySelector('[data-bpqv-variant-select]');
    const priceEl = card.querySelector('[data-bpqv-price]');
    const buyNow = card.querySelector('[data-bpqv-buy-now]');

    if (select && select.tagName === 'SELECT') {
      select.value = String(variant.id);
      const option = select.selectedOptions && select.selectedOptions[0];
      if (priceEl && option) {
        const priceHtml = option.getAttribute('data-price-html') || '';
        const compareHtml = option.getAttribute('data-compare-html') || '';
        priceEl.innerHTML = compareHtml ? `${priceHtml}<s>${compareHtml}</s>` : priceHtml;
      }
    } else if (select) {
      select.value = String(variant.id);
    }

    if (buyNow) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      buyNow.href = `${root}cart/${variant.id}:1?checkout`;
    }

    const available =
      variant.available !== false &&
      !(variant.inventory_management && Number(variant.inventory_quantity) <= 0);

    updateActionUi(card, available);

    card.querySelectorAll('.bpqv__size').forEach((el) => {
      const input = el.querySelector('input');
      if (input) el.classList.toggle('is-selected', input.checked);
    });
    card.querySelectorAll('.bpqv__swatch').forEach((el) => {
      const input = el.querySelector('input');
      if (input) el.classList.toggle('is-selected', input.checked);
    });
    card.querySelectorAll('.bpqv__option').forEach((optionEl) => {
      const label = optionEl.querySelector('[data-bpqv-selected-option]');
      const textEl = optionEl.querySelector('[data-bpqv-selected-text]');
      const checked = optionEl.querySelector('[data-product-option]:checked');
      if (!label || !checked) return;
      const next = checked.getAttribute('data-color-name') || checked.value;
      if (textEl) textEl.textContent = next;
      else label.textContent = next;
    });
  }

  function initGallery(card) {
    const stage = card.querySelector('[data-bpqv-stage]');
    const track = card.querySelector('[data-bpqv-track]');
    const slides = Array.from(card.querySelectorAll('[data-bpqv-slide]'));
    if (!stage || !track || slides.length === 0) return;

    const thumbs = Array.from(card.querySelectorAll('[data-bpqv-thumb]'));
    const thumbsEl = card.querySelector('[data-bpqv-thumbs]');
    const thumbsViewport = card.querySelector('[data-bpqv-thumbs-viewport]') || thumbsEl;
    const prevBtn = card.querySelector('[data-bpqv-thumb-prev]');
    const nextBtn = card.querySelector('[data-bpqv-thumb-next]');
    let index = 0;
    let dragX = 0;
    let startX = 0;
    let dragging = false;
    let width = Math.max(1, stage.clientWidth || stage.offsetWidth || 1);

    const measure = () => Math.max(1, stage.clientWidth || stage.offsetWidth || width || 1);

    const syncThumbNav = () => {
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= slides.length - 1;
    };

    const ensureThumbVisible = (thumb, instant) => {
      if (!thumbsViewport || !thumb) return;
      if (index === 0) {
        thumbsViewport.scrollTo({ left: 0, behavior: instant ? 'auto' : 'smooth' });
        return;
      }
      const viewRect = thumbsViewport.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      let delta = 0;
      if (thumbRect.left < viewRect.left + 2) {
        delta = thumbRect.left - viewRect.left;
      } else if (thumbRect.right > viewRect.right - 2) {
        delta = thumbRect.right - viewRect.right;
      }
      if (!delta) return;
      thumbsViewport.scrollTo({
        left: thumbsViewport.scrollLeft + delta,
        behavior: instant ? 'auto' : 'smooth',
      });
    };

    const setIndex = (next, { instant = false } = {}) => {
      index = Math.max(0, Math.min(slides.length - 1, next));
      width = measure();
      if (instant) track.classList.add('is-instant');
      else track.classList.remove('is-instant');
      track.style.transform = `translate3d(${-index * width}px, 0, 0)`;
      if (instant) {
        void track.offsetWidth;
        requestAnimationFrame(() => track.classList.remove('is-instant'));
      }

      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      ensureThumbVisible(thumbs[index], instant);
      syncThumbNav();
    };

    // Wait for modal layout so slide width is correct before first paint
    const startGallery = () => {
      setIndex(0, { instant: true });
      if (thumbsViewport) thumbsViewport.scrollLeft = 0;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(startGallery);
    });

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const i = Number(thumb.getAttribute('data-index') || 0);
        setIndex(i);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', (event) => {
        event.preventDefault();
        setIndex(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (event) => {
        event.preventDefault();
        setIndex(index + 1);
      });
    }

    const onPointerDown = (event) => {
      if (slides.length < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragging = true;
      startX = event.clientX;
      dragX = 0;
      width = measure();
      track.classList.add('is-instant');
      stage.classList.add('is-dragging');
      stage.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!dragging) return;
      dragX = event.clientX - startX;
      track.style.transform = `translate3d(${-index * width + dragX}px, 0, 0)`;
    };

    const onPointerUp = (event) => {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-dragging');
      track.classList.remove('is-instant');
      const threshold = Math.min(56, width * 0.15);
      if (dragX <= -threshold) setIndex(index + 1);
      else if (dragX >= threshold) setIndex(index - 1);
      else setIndex(index);
      try {
        stage.releasePointerCapture?.(event.pointerId);
      } catch (e) {}
    };

    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    stage.addEventListener('pointercancel', onPointerUp);

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            setIndex(index, { instant: true });
          })
        : null;
    if (ro) ro.observe(stage);

    card._bpqvGallery = { setIndex, destroy: () => ro && ro.disconnect() };
  }

  const HEART_OUTLINE =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" focusable="false"><path d="M12.1 21.35l-1.1-.99C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.9 11.86l-1 0.99z" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linejoin="round"></path></svg>';
  const HEART_FILLED =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M12.1 21.35l-1.1-.99C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.9 11.86l-1 0.99z" fill="currentColor" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>';

  function wishlistLabel(el) {
    return String(el?.getAttribute?.('aria-label') || el?.getAttribute?.('title') || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function cardHeartSaved(el) {
    const label = wishlistLabel(el);
    if (label.includes('remove from wishlist') || label.includes('saved') || label.includes('in wishlist')) return true;
    if (label.includes('add to wishlist')) return false;
    return el?.getAttribute?.('aria-pressed') === 'true';
  }

  function findArticleCardHeart(productId) {
    if (!productId) return null;
    const card = document.querySelector(`[data-article-pcard][data-product-id="${CSS.escape(String(productId))}"]`);
    if (!card) return null;
    return (
      card.querySelector('[data-product-card-wishlist-slot] button, [data-product-card-wishlist-slot] [role="button"]') ||
      Array.from(card.querySelectorAll('button, [role="button"]')).find((el) => wishlistLabel(el).includes('wishlist')) ||
      null
    );
  }

  function paintWishlist(button, saved) {
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
    button.innerHTML = saved ? HEART_FILLED : HEART_OUTLINE;
  }

  function bindWishlist(card) {
    const button = card.querySelector('[data-bpqv-wishlist]');
    if (!button || button.dataset.bpqvWishlistBound === 'true') return;
    button.dataset.bpqvWishlistBound = 'true';

    const productId = button.getAttribute('data-product-id') || card.getAttribute('data-product-id');
    const sync = () => {
      const heart = findArticleCardHeart(productId);
      if (heart) paintWishlist(button, cardHeartSaved(heart));
    };

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const nextSaved = !button.classList.contains('is-saved');
      paintWishlist(button, nextSaved);
      const heart = findArticleCardHeart(productId);
      if (heart) heart.click();
      [350, 900].forEach((ms) => setTimeout(sync, ms));
    });

    sync();
    [400, 1200].forEach((ms) => setTimeout(sync, ms));
  }

  function bindCard(card, host) {
    if (card.dataset.bpqvBound === 'true') return;
    card.dataset.bpqvBound = 'true';

    const variants = parseVariants(card);
    initGallery(card);

    const selected = getSelectedOptions(card);
    const initial = findVariant(variants, selected) || variants.find((v) => v.available) || variants[0];
    if (initial) updateCardState(card, initial);
    bindWishlist(card);

    card.addEventListener('change', (event) => {
      if (!event.target.matches('[data-product-option]')) return;
      const next = findVariant(variants, getSelectedOptions(card));
      updateCardState(card, next);
    });

    card.addEventListener(
      'click',
      (event) => {
        const bis = event.target.closest('[data-bpqv-bis-opener]');
        if (bis) {
          ensureComponents().then(() => syncSharedBisModal(card));
          return;
        }

        const swapBtn = event.target.closest('[data-bpqv-product-swap]');
        if (!swapBtn || !host) return;
        event.preventDefault();
        const nextHandle = swapBtn.getAttribute('data-bpqv-product-swap');
        if (!nextHandle) return;
        host.setAttribute('data-product-handle', nextHandle);
        host.mount({ force: true });
      },
      true
    );
  }

  class BlogProductQuickview extends HTMLElement {
    connectedCallback() {
      if (!this.classList.contains('bpqv')) this.classList.add('bpqv');
    }

    async mount({ force = false } = {}) {
      if (this._mounting) return;
      if (this.dataset.bpqvMounted === 'true' && !force) return;

      this._mounting = true;
      this.dataset.bpqvMounted = 'true';

      const handle = getHandle(this);
      if (!handle) {
        this.innerHTML = '<div class="bpqv__error">Missing product handle.</div>';
        this._mounting = false;
        return;
      }

      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      this.classList.toggle('bpqv--hide-colors', this.dataset.showColors !== 'true');

      try {
        const card = await fetchQuickviewHtml(handle);
        this.innerHTML = '';
        this.appendChild(card);
        bindCard(card, this);
        // BIS components are not needed for first paint
        ensureComponents().catch(() => {});
      } catch (error) {
        console.error(error);
        this.innerHTML = '<div class="bpqv__error">Unable to load this product.</div>';
      } finally {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
        this._mounting = false;
      }
    }
  }

  if (!customElements.get('blog-product-quickview')) {
    customElements.define('blog-product-quickview', BlogProductQuickview);
  }

  window.BlogProductQuickviewPrefetch = function prefetch(handle) {
    if (!handle || htmlCache.has(handle) || inflight.has(handle)) return;
    fetchQuickviewHtml(handle).catch(() => {});
  };

  window.BlogProductQuickviewHasCache = function hasCache(handle) {
    return Boolean(handle && htmlCache.has(handle));
  };
})();
